import JSZip from 'jszip';

/**
 * Härleder rätt MIME-typ för förhandsvisning utifrån filnamnet.
 *
 * Dokument-bloben hämtas utan tillförlitlig Content-Type (lagringsdrivaren
 * returnerar `application/octet-stream`/`text/plain`), så en blob utan typ
 * visas som rå text i en `<iframe>` i stället för att renderas som t.ex. PDF.
 * Genom att sätta blobbens typ enligt filändelsen renderas PDF:er och bilder
 * korrekt i förhandsvisningen.
 */

const PREVIEW_MIME_TYPES: Record<string, string> = {
  '.pdf': 'application/pdf',
  '.png': 'image/png',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.gif': 'image/gif',
};

function extensionOf(fileName: string): string {
  const idx = fileName.lastIndexOf('.');
  return idx >= 0 ? fileName.slice(idx).toLowerCase() : '';
}

/** Returnerar MIME-typ för förhandsvisning, eller undefined om okänd. */
export function previewMimeType(fileName: string): string | undefined {
  return PREVIEW_MIME_TYPES[extensionOf(fileName)];
}

/**
 * Bygger en blob med korrekt MIME-typ inför förhandsvisning. Om filändelsen är
 * okänd returneras originalbloben oförändrad.
 */
export function typedPreviewBlob(blob: Blob, fileName: string): Blob {
  const type = previewMimeType(fileName);
  return type ? new Blob([blob], { type }) : blob;
}

/**
 * Extraherar textinnehållet ur en PowerPoint-fil (.ppt/.pptx) och bygger en
 * enkel HTML-representation (en ruta per slide) för förhandsvisning.
 */
export async function parsePptxToHtml(arrayBuffer: ArrayBuffer): Promise<string> {
  const zip = await JSZip.loadAsync(arrayBuffer);
  const slideFiles = Object.keys(zip.files)
    .filter(name => /^ppt\/slides\/slide\d+\.xml$/.test(name))
    .sort((a, b) => {
      const numA = parseInt(a.match(/slide(\d+)/)?.[1] || '0');
      const numB = parseInt(b.match(/slide(\d+)/)?.[1] || '0');
      return numA - numB;
    });

  if (slideFiles.length === 0) {
    return '<p>Inga slides hittades.</p>';
  }

  let html = '';
  for (let i = 0; i < slideFiles.length; i++) {
    const xmlStr = await zip.files[slideFiles[i]].async('text');
    const texts: string[] = [];
    // Extract all <a:t> text nodes from the slide XML
    const regex = /<a:t[^>]*>([\s\S]*?)<\/a:t>/g;
    let match;
    while ((match = regex.exec(xmlStr)) !== null) {
      const text = match[1].trim();
      if (text) texts.push(text);
    }

    html += `<div style="border:1px solid #dee2e6;border-radius:8px;padding:1.5rem;margin-bottom:1rem;background:#f8f9fa;">`;
    html += `<div style="font-size:0.85rem;color:#6c757d;margin-bottom:0.75rem;font-weight:600;">Slide ${i + 1}</div>`;
    if (texts.length > 0) {
      html += texts.map(t => `<p style="margin:0.25rem 0">${t.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</p>`).join('');
    } else {
      html += `<p style="color:#adb5bd;font-style:italic;">Tom slide</p>`;
    }
    html += `</div>`;
  }
  return html;
}
